<template>
  <section class="card-container survey-widget">
    <header class="card-container-header">{{ question }}</header>
    <div class="card">
      <div v-if="status === 'loading'" class="survey-widget__status">
        <span class="survey-widget__loading">Loading...</span>
      </div>
      <template
        v-else-if="
          status === 'idle' ||
          status === 'already_answered' ||
          status === 'unauthenticated'
        "
      >
        <ul class="survey-widget__answers-list">
          <li
            v-for="(answer, index) in answers"
            :key="index"
            class="survey-widget__answer-item"
          >
            <button
              type="button"
              class="survey-widget__answer-button"
              :class="{
                'survey-widget__answer-button--selected':
                  (status === 'already_answered' &&
                    answer === previousAnswer) ||
                  (status === 'unauthenticated' &&
                    pendingAnswerIndex === index + 1),
              }"
              :disabled="status === 'already_answered'"
              @click="handleAnswerClick(index + 1)"
            >
              {{ answer }}
            </button>
          </li>
        </ul>
        <div
          v-if="status === 'unauthenticated' && pendingAnswerIndex !== null"
          class="survey-widget__subscribe-section subscribe-form"
        >
          <p class="card-container-header">
            Subscribe to submit your response:
          </p>
          <SubscribeFormContainer
            :newsletter="newsletter"
            :subscriber-facing-authentication="subscriberFacingAuthentication"
            variant="archive_page"
            :on-subscribe="handleSubscribe"
          />
        </div>
      </template>
      <div v-else-if="status === 'submitting'" class="survey-widget__status">
        <span class="survey-widget__loading">Submitting...</span>
      </div>
      <div v-else-if="status === 'success'" class="survey-widget__status">
        <span class="survey-widget__success">Thanks for your response!</span>
      </div>
      <div v-else-if="status === 'error'" class="survey-widget__status">
        <span class="survey-widget__error">{{ errorMessage }}</span>
        <button
          type="button"
          class="survey-widget__retry"
          @click="status = 'idle'"
        >
          Try again
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import Urls from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

import type {
  BasicSubscriber,
  SubscriberFacingAuthentication,
} from "../SubscribeForm/lib";
import SubscribeFormContainer from "../SubscribeForm/SubscribeFormContainer.vue";

type SurveyStatus =
  | "loading"
  | "idle"
  | "submitting"
  | "success"
  | "already_answered"
  | "error"
  | "unauthenticated";

const props = defineProps<{
  surveyId: string;
  emailId: string;
  question: string;
  answers: string[];
  subscriber: BasicSubscriber | undefined;
  newsletter: Newsletter;
  subscriberFacingAuthentication: SubscriberFacingAuthentication;
  // Optional props for stories/testing to override initial state
  initialStatus?: SurveyStatus;
  initialPreviousAnswer?: string;
  initialErrorMessage?: string;
}>();

const getInitialStatus = (): SurveyStatus => {
  if (props.initialStatus) return props.initialStatus;
  return props.subscriber ? "loading" : "unauthenticated";
};

const status = ref<SurveyStatus>(getInitialStatus());
const errorMessage = ref(props.initialErrorMessage || "");
const currentSubscriber = ref<BasicSubscriber | undefined>(props.subscriber);
const previousAnswer = ref(props.initialPreviousAnswer || "");
const pendingAnswerIndex = ref<number | null>(null);

const checkExistingResponse = async () => {
  if (!currentSubscriber.value) {
    status.value = "unauthenticated";
    return;
  }

  try {
    const url = new URL(
      Urls["api:retrieve_survey_responses"](),
      window.location.origin
    );
    url.searchParams.set("survey", props.surveyId);
    url.searchParams.set("subscriber_id", currentSubscriber.value.id);

    const response = await fetch(url.toString());
    if (response.ok) {
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const existingResponse = data.results[0];
        previousAnswer.value = existingResponse.answer;
        status.value = "already_answered";
        return;
      }
    }
    status.value = "idle";
  } catch {
    status.value = "idle";
  }
};

const handleAnswerClick = (answerIndex: number) => {
  if (status.value === "unauthenticated") {
    pendingAnswerIndex.value = answerIndex;
  } else {
    submitAnswer(answerIndex);
  }
};

const handleSubscribe = async (newSubscriber: BasicSubscriber) => {
  currentSubscriber.value = newSubscriber;
  // If user had selected an answer before subscribing, submit it now
  if (pendingAnswerIndex.value !== null) {
    await submitAnswer(pendingAnswerIndex.value);
    pendingAnswerIndex.value = null;
  } else {
    checkExistingResponse();
  }
};

const submitAnswer = async (answerIndex: number) => {
  if (!currentSubscriber.value) {
    status.value = "unauthenticated";
    return;
  }

  status.value = "submitting";
  errorMessage.value = "";

  try {
    const response = await fetch(Urls["api:retrieve_survey_responses"](), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        survey_id: props.surveyId,
        subscriber_id: currentSubscriber.value.id,
        email_id: props.emailId,
        answer: answerIndex,
      }),
    });

    if (response.status === 201) {
      status.value = "success";
    } else {
      const data = await response.json();
      if (data.code === "invalid_input" && data.detail?.includes("already")) {
        // User tried to submit but already answered - show their attempted answer
        previousAnswer.value = props.answers[answerIndex - 1] || "";
        status.value = "already_answered";
      } else {
        status.value = "error";
        errorMessage.value = data.detail || "Failed to submit response";
      }
    }
  } catch {
    status.value = "error";
    errorMessage.value = "Network error. Please try again.";
  }
};

onMounted(() => {
  // Skip API call when initial status is provided (for stories/testing)
  if (props.initialStatus) return;
  if (props.subscriber) {
    checkExistingResponse();
  }
});
</script>

<style>
.survey-widget__answers-list {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  list-style: none;
  margin: 0;
  padding: 0;
}

.survey-widget__answer-item {
  padding-left: 0;
}

.survey-widget__answer-button {
  background: var(--color-background);
  border: 1px solid var(--color-background-offset);
  border-radius: calc(var(--space) / 2);
  color: inherit;
  cursor: pointer;
  display: block;
  font-family: inherit;
  font-size: inherit;
  padding: 0.75em 1em;
  text-align: left;
  transition: background-color 0.15s, border-color 0.15s;
  width: 100%;
}

.survey-widget__answer-button:hover:not(:disabled) {
  background: var(--color-background-offset);
  border-color: var(--color-action);
}

.survey-widget__answer-button:focus {
  outline: 2px solid var(--color-action);
  outline-offset: 2px;
}

.survey-widget__answer-button:disabled {
  cursor: default;
  opacity: 0.6;
}

.survey-widget__answer-button--selected {
  background: color-mix(
    in srgb,
    var(--color-action) 15%,
    var(--color-background)
  );
  border-color: var(--color-action);
  border-width: 2px;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-action) 20%, transparent);
  font-weight: 500;
  opacity: 1;
}

.survey-widget__status {
  text-align: center;
}

.survey-widget__subscribe-section .card-container-header {
  padding-bottom: 0;
  margin-top: var(--space);
}

.survey-widget__subscribe-section .card {
  border: 0;
  margin-bottom: 0;
  margin-top: 0;
}

.survey-widget__subscribe-section {
  border-top: 1px solid var(--color-background-offset);
  margin-top: 1em;
  margin-left: calc(var(--space) * -1);
  margin-right: calc(var(--space) * -1);
  padding-left: calc(var(--space));
  padding-right: calc(var(--space));
}

.survey-widget__loading {
  color: var(--color-foreground-offset);
}

.survey-widget__success {
  color: var(--color-success, #28a745);
}

.survey-widget__error {
  color: var(--color-error, #dc3545);
}

.survey-widget__retry {
  background: var(--color-action);
  border: none;
  border-radius: calc(var(--space) / 2);
  color: white;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  margin-top: 0.5em;
  padding: 0.5em 1em;
}

.survey-widget__retry:hover {
  opacity: 0.9;
}
</style>
